import { FormService, NumberInputFrom, AngleFrom } from '@tencent/wb-ext-form';
import { InjectorService, ColorFrom } from '@tencent/workbench';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import BaseToolbarFeature from './base-toolbar-feature';
import { genCssColor } from '@utils/css';
import get from 'lodash/get';
import { checkIsType, getSelectedModelTypeArr, opacityToAlpha, validNumber } from '@framework/workbench/drawer/utils';
import { IOptions, TdwDrawerConstMap, tdwDrawerReport, tdwDrawerReportColor, tdwDrawerReportAngle, IReportInfo, NumberInputFromMap } from '@framework/workbench/drawer/report';
import { getColorParams } from '@framework/workbench/drawer/utils/color-options';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { getColorRGB } from '@src/slide/infrastructure/module/independent/color/transform';
import { objectCompareLite } from '@src/slide/infrastructure/module/independent/object-operation';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { ImageTypes } from '@src/slide/infrastructure/module/dependent/image/constant/image-type';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { ESelectType, IBaseDrawerFeature } from './base-drawer-interface';
import { getMenuValues } from '@src/slide/application/service/menu-manager/menu-values';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { ITextEditorStateService } from '@src/slide/application/service/text-editor-state-service/text-editor-state-service.interface';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { IMultiDiffValue, MULTI_DIFF_VALUE } from '@framework/workbench/drawer/constants';
import { IInputParams } from '@src/slide/application/service/drawer-input-manager/drawer-handler';

export default class BaseDrawerFeature extends BaseToolbarFeature implements IBaseDrawerFeature {
    public formService: FormService;
    public tdwDrawerConstMap = TdwDrawerConstMap;
    public numberInputFromMap = NumberInputFromMap;
    public numberInputValue = {}; // 各个feature的自己的numberInputValue
    public initPropsCallback: () => void;

    public constructor() {
        super();
        this.formService = InjectorService.get(FormService);
    }
    /**
     * 获取侧边栏的值
     */
    public getPropertyMenu() {
        return this.getAppService().editableServiceLayer?.menuManager.propertyMenu(this.getSelectModel());
    }

    /**
     * 获取当前按键的值
     */
    public getItemValue(id?) {
        const values = this.getToolbarMenu()?.values;
        return values?.[id || this.id] || '';
    }

    /**
     * 获取当前选中元素
     */
    public getSelectModel() {
        // toUpateModel的更新时机在PropertyEditorManager中，通过监听选中元素的变更来更新
        // 但可能出现比如页面刚加载时，选中元素打开侧边栏，但由于PropertyEditorManager是异步加载还未注册监听事件，导致toUpateModel为undefined
        if (!this.getDrawerInputManager()?.toUpateModel) {
            this.getDrawerInputManager()?.updateCurrentModel(this.getAppService()
                .viewableServiceLayer.selectionService.getSelectedModel());
        }
        return this.getDrawerInputManager()?.toUpateModel;
    }

    public getEditingStateManager() {
        return this.getAppService().viewableServiceLayer.editingStateManager;
    }

    public checkSelectType = (): ESelectType => {
        const selectedSlideElements = this.getSelectionService().selectedElements
            .selectedArrayForType(SelectedEleMgrType.SLIDE);
        const groupSelectedUIModelArr = this.getSelectionService().selectedElements
            .selectedArrayForType(SelectedEleMgrType.GROUP);
        if (selectedSlideElements.length > 1) return ESelectType.MULTY_SLIDE_OR_GROUP_HAS_CHILDREN;
        if (selectedSlideElements.length < 1) return ESelectType.NO_SLIDE_OR_OTHER;
        const element = selectedSlideElements[0] as IElementBaseModel;
        if ([UIModelType.SHAPE, UIModelType.IMAGE, UIModelType.CONNECTOR].includes(element.type)) {
            return ESelectType.SHAPE_OR_IMAGE_OR_CONNECTOR;
        }
        if (element.type === UIModelType.GROUP) {
            return groupSelectedUIModelArr.length > 1
                ? ESelectType.MULTY_SLIDE_OR_GROUP_HAS_CHILDREN
                : ESelectType.GROUP_NO_CHILD;
        }
        return ESelectType.NO_SLIDE_OR_OTHER;
    };

    public updateProps = (
        id: string, value, onlyChangeUI?: boolean, onlyPreview?: boolean,
        reportInfo?: IReportInfo,
    ) => {
        if (onlyChangeUI) {
            value.onlyChangeUI = true;
        }
        if (onlyPreview) {
            value.onlyPreview = true;
        }
        this.getPropertyMenu()?.callback('', id, value, { onlyChangeUI, onlyPreview });
        if (reportInfo) {
            this.tdwDrawerReport(reportInfo.reportId, { ...reportInfo });
        }
    };

    public isMultiOrGroup(): boolean {
        const model = this.getSelectModel();
        return model?.type === UIModelType.MULTIPLY || model?.type === UIModelType.GROUP;
    }

    public isMultiModel(): boolean {
        return this.getSelectModel()?.type === UIModelType.MULTIPLY;
    }

    public isEmptyModel(): boolean {
        return this.getSelectModel()?.type === UIModelType.EMPTY;
    }

    public isMultiDiffValue(key: string, excludeProp?: string[]) {
        if (!this.isMultiModel()) return false;
        const { selectedModels } = this.getSelectModel() as IMultiplyModel;
        const valuesArray: any[] = [];
        selectedModels.forEach((model) => {
            if (model.type !== UIModelType.TABLE && model.type !== UIModelType.CHART) {
                valuesArray.push(getMenuValues(model));
            }
        });
        const { length } = valuesArray;
        if (length < 1) return false;
        const itemValue = get(valuesArray[0], key);
        for (let index = 1; index < length; index++) {
            const isObjEqual = objectCompareLite(itemValue, get(valuesArray[index], key), excludeProp as any);
            if (!isObjEqual) return true;
        }
        return false;
    }

    public getAlphaFromColor = (color: string): number => {
        const rgba = getColorRGB(color);
        if (rgba instanceof Array) {
            if (rgba.length === 4) {
                return rgba[3];
            }
        }
        return 1;
    };

    public get imageType(): string {
        // @ts-ignore
        return this.getPropertyMenu().values?.imageType || '';
    }

    public isGifOrSvg = (): boolean => this.imageType === ImageTypes.SVG
    || this.imageType === ImageTypes.GIF;

    public isGif = (): boolean => this.imageType === ImageTypes.GIF;

    public tdwDrawerReport = (formItemKey: string, options?: IOptions, onlyChangeUI?: boolean) => {
        if (onlyChangeUI) return;
        tdwDrawerReport(formItemKey, options);
    };

    public tdwDrawerReportColor = (formItemKey: string, from: ColorFrom | number, onlyChangeUI?: boolean) => {
        if (onlyChangeUI) return;
        tdwDrawerReportColor(formItemKey, from);
    };

    public tdwDrawerReportAngle = (formItemKey: string, from?: AngleFrom, value?: number, onlyChangeUI?: boolean) => {
        if (onlyChangeUI) return;
        tdwDrawerReportAngle(formItemKey, from, value);
    };

    public tdwDrawerReportNumberInput(
        reportId: string, options?: IOptions,
        from?: NumberInputFrom, onlyChangeUI?: boolean,
    ) {
        if (onlyChangeUI) return;
        const optionsVer5 = (from && this.numberInputFromMap[from]) ? { ver5: this.numberInputFromMap[from] } : { };
        this.tdwDrawerReport(reportId, { ...optionsVer5, ...options });
    }

    public isTable = () => this.checkIsType(UIModelType.TABLE);
    public isConnector = () => this.checkIsType(UIModelType.CONNECTOR);
    public isBackground = () => this.getSelectModel()?.type === UIModelType.EMPTY;

    public getRgbaColor = (color: string, opacity: number): string => {
        const [R, G, B] = getColorRGB(color);
        const alpha = opacityToAlpha(opacity);
        return genCssColor(R, G, B, alpha);
    };

    public getColorOptions = (value: string) => getColorParams(value);

    public checkIsType = (type: string, model?) => checkIsType(model ?? this.getSelectModel(), type);

    public getSelectedModelTypeArr = model => getSelectedModelTypeArr(model);

    public validNumber = (value: number, max = 100, min = 0): number => validNumber(value, max, min);

    /**
     * 矫正输入的值，如果是输入框输入的超出范围的值，显示输入的值，但更新sdk时要为范围内的值
     * @param value 变化的值
     * @param from 操作输入框框组件的方式
     * @param max 最大值
     * @param min 最小值
     * @returns object
     */
    public getValidValue(
        value: Record<string, number>, from?: NumberInputFrom | AngleFrom,
        max?: number, min?: number,
    ) {
        this.getDrawerInputManager()!.changeFrom = from;
        if (from !== NumberInputFrom.INPUT) {
            this.getDrawerInputManager()?.clearInputValueCache();
            return value;
        }
        this.getDrawerInputManager()?.updateInputStateReInput();
        const [propName] = Object.keys(value);
        const propValue = expect(value[propName]).isNull() ? NaN : value[propName];
        if (this.getDrawerInputManager()?.isFocus) {
            // 缓存输入的值
            this.getDrawerInputManager()!.numberInputValue[propName] = propValue;
        }
        // 返回修正的值
        return { [propName]: validNumber(propValue, max, min) };
    }

    /**
     * 获取显示的值，特殊情况如果为输入框输入的值，则不限制范围直接显示
     * 如果返回的值为NaN，表明为多选且值不时做的显示值
     */
    public getShownValue(key: string, value: Record<string, number>): number | IMultiDiffValue {
        const [propName] = Object.keys(value);
        const shownValue = this.getDrawerInputManager()?.getCachedInputValue(key, propName) ?? value[propName];
        return isNaN(shownValue) ? MULTI_DIFF_VALUE : shownValue;
    }

    public getFillValue = (): ITDColor | undefined => {
        const curModel = this.getSelectModel() as IElementBaseModel;
        return curModel?.getBackgroundFill()
            .getShownTdColor();
    };

    public modifyShownValue = (shownValue: number | '', toFixed: number): number | '' => {
        if (shownValue === '' || isNaN(shownValue)) {
            return shownValue;
        }
        return parseFloat(Number(shownValue).toFixed(toFixed));
    };

    public updateDropdownVisible(visible: boolean, id) {
        const drawerStateIns = this.getAppService().editableServiceLayer?.drawerStateManager?.dangerousInstance;
        if (!drawerStateIns) return;
        if (visible) {
            drawerStateIns.isDrawerMenuShown = id;
            return;
        }
        if (drawerStateIns.isDrawerMenuShown === id) {
            drawerStateIns.isDrawerMenuShown = false;
        }
    }

    /**
     * 输入框组件是否仅更新UI
     * @returns
     */
    public isInputOnlyChangeUI = (): boolean => {
        const drawerInputManager = this.getDrawerInputManager();
        drawerInputManager!.isOnlyChangeUI = !!(drawerInputManager?.isInputting && !drawerInputManager?.isForceUpdate);
        return drawerInputManager!.isOnlyChangeUI;
    };

    /**
     * 带滑块或者旋转盘的输入框组件是否仅更新UI
     * @param dragEnd 是否拖拽结束
     * @returns
     */
    public isInputOnlyChangeUIWithDrag = (dragEnd?: boolean): boolean => {
        const drawerInputManager = this.getDrawerInputManager();
        drawerInputManager!.isOnlyChangeUI = !!(!drawerInputManager?.isForceUpdate
            && (drawerInputManager?.isInputting || !dragEnd));
        return this.getDrawerInputManager()!.isOnlyChangeUI;
    };

    public getDrawerInputManager = () => this.getAppService().editableServiceLayer
        ?.drawerInputManager.dangerousInstance;


    public getInputParams(): IInputParams {
        return { key: '' };
    }

    /**
     * 添加初始化方法回调
     * @param key 用来标识对应的feature
     */
    protected addInitPropsCallback = (key: string) => {
        this.getDrawerInputManager()?.addInitPropsCallback(key, this.initPropsCallback);
    };

    protected getTextEditorStateService(): ITextEditorStateService {
        return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
            .viewableServiceLayer.textEditorStateService;
    }

    protected getAppServices(): IEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
    }

    protected getToolbarMenu() {
        return this.getMenuManager()?.toolbarMenu(this.getSelectModel());
    }
}
